import { RefObject, useEffect, useRef, useState } from "react";
import { findPrecedingSectionHeading } from "./sectionHeuristics";
import { collectPrecedingBlockLines, normalizeSelectedText } from "./textSelection.utils";
import { SectionResolver, SelectedTextResult, TextSelectionDomConfig } from "./textSelection.types";

export interface SelectionAnchor {
    getBoundingClientRect: () => DOMRect;
    contextElement?: Element;
}

const MIN_SELECTION_LENGTH = 2;

function closestMatching(node: Node, selector: string, root: HTMLElement): HTMLElement | null {
    const element = node instanceof HTMLElement ? node : node.parentElement;
    const match = element?.closest<HTMLElement>(selector) ?? null;
    return match && root.contains(match) ? match : null;
}

function readSelectionWithinPage(range: Range, root: HTMLElement, domConfig: TextSelectionDomConfig) {
    const startTextRoot = closestMatching(range.startContainer, domConfig.textRootSelector, root);
    const endTextRoot = closestMatching(range.endContainer, domConfig.textRootSelector, root);
    if (!startTextRoot || !endTextRoot) {
        return undefined;
    }

    const startPage = closestMatching(range.startContainer, domConfig.pageSelector, root);
    const endPage = closestMatching(range.endContainer, domConfig.pageSelector, root);
    // A selection crossing a page boundary has no single section, and in the virtualised PDF list one
    // of its two ends can be unmounted at any moment. Ignoring it is simpler than guessing.
    if (!startPage || startPage !== endPage) {
        return undefined;
    }

    return { textRoot: startTextRoot, page: startPage };
}

function readPageNumber(page: HTMLElement): number | undefined {
    const attribute = page.getAttribute("data-page-number") ?? page.getAttribute("data-text-page");
    const pageNumber = attribute ? Number(attribute) : NaN;
    return Number.isNaN(pageNumber) ? undefined : pageNumber;
}

/**
 * Gecko builds `Selection.toString()` from what is rendered, and the pdf.js text layer draws its spans
 * fully transparent on top of the canvas, so in Firefox it comes back empty for every pdf selection while
 * the Range still carries the text. Reading it back per element keeps the line breaks those spans stand
 * for, which `joinLines` then turns into spaces - a plain `Range.toString()` concatenates the text nodes
 * and runs the last word of one line into the first word of the next.
 */
function readSelectedText(selection: Selection, range: Range): string {
    const rendered = selection.toString();
    if (rendered.length > 0) {
        return rendered;
    }

    const walker = document.createTreeWalker(range.cloneContents(), NodeFilter.SHOW_TEXT);
    const lines: Array<string> = [];
    let currentParent: Node | null = null;
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (node.parentNode !== currentParent) {
            currentParent = node.parentNode;
            lines.push("");
        }
        lines[lines.length - 1] += node.nodeValue ?? "";
    }

    return lines.join("\n");
}

function sectionFromRenderedBlocks(textRoot: HTMLElement, startNode: Node): string | undefined {
    const { lines, startLineIndex } = collectPrecedingBlockLines(textRoot, startNode);
    return findPrecedingSectionHeading(lines, startLineIndex);
}

/**
 * Watches for a text selection inside a viewer and reports it, along with a Popper anchor that tracks the
 * selection as the document scrolls. `frozen` stops the watcher from clearing what it captured, which the
 * caller needs while the user is editing the captured text in a field of its own.
 */
export function useTextSelectionCapture(
    rootRef: RefObject<HTMLElement | null>,
    domConfig: TextSelectionDomConfig,
    options: { resetKey?: string; frozen: boolean; resolveSection?: SectionResolver }
) {
    const [captured, setCaptured] = useState<SelectedTextResult | undefined>(undefined);
    const [anchorEl, setAnchorEl] = useState<SelectionAnchor | undefined>(undefined);
    const frozenRef = useRef(options.frozen);
    frozenRef.current = options.frozen;
    // Held in a ref so a resolver rebuilt on every render never re-registers the document listeners.
    const resolveSectionRef = useRef(options.resolveSection);
    resolveSectionRef.current = options.resolveSection;

    const clear = () => {
        setCaptured(undefined);
        setAnchorEl(undefined);
    };

    useEffect(() => {
        const root = rootRef.current;
        if (!root) {
            return;
        }

        // Listening on the document rather than the root: a drag that starts on the page and is released
        // over the toolbar never produces a pointerup on the root.
        const handlePointerUp = () => {
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
                return;
            }

            const range = selection.getRangeAt(0);
            const location = readSelectionWithinPage(range, root, domConfig);
            if (!location) {
                return;
            }

            const clause = normalizeSelectedText(readSelectedText(selection, range), { joinLines: domConfig.joinLines });
            if (clause.length < MIN_SELECTION_LENGTH) {
                return;
            }

            const pageNumber = readPageNumber(location.page);
            const resolveSection = resolveSectionRef.current;

            setCaptured({
                clause,
                // A viewer that can consult the document's own structure (a PDF against its extracted
                // markdown) resolves the section itself; the rest read it off their rendered headings.
                section: resolveSection ? resolveSection(clause, pageNumber) : sectionFromRenderedBlocks(location.textRoot, range.startContainer),
                pageNumber,
            });
            const page = location.page;
            setAnchorEl({
                // Vertically on the selection, horizontally on the whole page, so the button lands in the
                // margin beside the document rather than up against the text it belongs to.
                getBoundingClientRect: () => {
                    const selectionRect = range.getBoundingClientRect();
                    // A collapsed rect is how a scrolled-away or virtualised-out selection reports itself,
                    // and Popper's hide modifier needs to see that, so it is passed through untouched.
                    if (selectionRect.width === 0 && selectionRect.height === 0) {
                        return selectionRect;
                    }
                    const pageRect = page.getBoundingClientRect();
                    return new DOMRect(pageRect.left, selectionRect.top, pageRect.width, selectionRect.height);
                },
                contextElement: root,
            });
        };

        const handleSelectionChange = () => {
            if (frozenRef.current) {
                return;
            }
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed) {
                clear();
            }
        };

        document.addEventListener("pointerup", handlePointerUp);
        document.addEventListener("selectionchange", handleSelectionChange);
        return () => {
            document.removeEventListener("pointerup", handlePointerUp);
            document.removeEventListener("selectionchange", handleSelectionChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootRef, domConfig.pageSelector, domConfig.textRootSelector, domConfig.joinLines]);

    // Zooming rebuilds the text layer and switching documents replaces it, which detaches the range the
    // anchor is measuring.
    useEffect(() => {
        clear();
    }, [options.resetKey]);

    return { captured, anchorEl, clear };
}
